import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, ExternalLink, ArrowRight, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { CLOSED_MATCH_STATUSES } from "@/utils/matches/status";

interface TransitionMatchesModalProps {
	open: boolean;
	onClose: () => void;
	fromStage: string;
	toStage: string;
	dateFrom: string | null;
	dateTo: string | null;
	userId: string | null;
}

interface MatchTransitionRow {
	matchId: string;
	candidateName: string;
	jobTitle: string;
	score: number | null;
	currentStatus: string;
	transitionDate: string;
	history: { from: string; to: string; date: string }[];
}

const STATUS_BADGE_COLORS: Record<string, string> = {
	applied: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
	matched: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300",
	screening: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
	sent: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
	interview: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300",
	offer: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
	hired: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
};

function getStatusBadgeClass(status: string): string {
	if (CLOSED_MATCH_STATUSES.includes(status as any))
		return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300";
	return STATUS_BADGE_COLORS[status] || "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
}

export function TransitionMatchesModal({
	open,
	onClose,
	fromStage,
	toStage,
	dateFrom,
	dateTo,
	userId,
}: TransitionMatchesModalProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState<MatchTransitionRow[]>([]);
	const [expandedMatch, setExpandedMatch] = useState<string | null>(null);

	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			// 1. Fetch history_matches for this specific transition
			let histQuery = supabase
				.from("history_matches" as any)
				.select("match_id, changed_at, old_value, new_value, changed_by")
				.order("changed_at", { ascending: false });

			if (dateFrom)
				histQuery = histQuery.gte("changed_at", dateFrom);
			if (dateTo)
				histQuery = histQuery.lte("changed_at", dateTo);
			if (userId)
				histQuery = histQuery.eq("changed_by", userId);

			const { data: histData, error: histError } = await histQuery;
			if (histError) throw histError;

			// Filter to matching transitions
			const matchingTransitions = (histData || []).filter((row: any) => {
				const from = row.old_value?.status;
				const to = row.new_value?.status;
				return from === fromStage && to === toStage;
			});

			// Deduplicate by match_id (keep latest transition)
			const matchIdMap = new Map<string, any>();
			matchingTransitions.forEach((row: any) => {
				if (!matchIdMap.has(row.match_id))
					matchIdMap.set(row.match_id, row);
			});

			const matchIds = Array.from(matchIdMap.keys());
			if (matchIds.length === 0) {
				setRows([]);
				return;
			}

			// 2. Fetch match details with candidate and job
			const { data: matchData, error: matchError } = await supabase
				.from("matches")
				.select(`
					id,
					score,
					status,
					candidate_id,
					job_id,
					candidates(id, name),
					jobs(id, title)
				`)
				.in("id", matchIds);

			if (matchError) throw matchError;

			// 3. Fetch full status history for these matches
			const { data: fullHistory, error: fullHistError } = await supabase
				.from("history_matches" as any)
				.select("match_id, old_value, new_value, changed_at")
				.in("match_id", matchIds)
				.order("changed_at", { ascending: true });

			if (fullHistError) throw fullHistError;

			// Group history by match
			const historyByMatch = new Map<string, { from: string; to: string; date: string }[]>();
			(fullHistory || []).forEach((h: any) => {
				const from = h.old_value?.status;
				const to = h.new_value?.status;
				if (!from || !to) return;
				const list = historyByMatch.get(h.match_id) || [];
				list.push({ from, to, date: h.changed_at });
				historyByMatch.set(h.match_id, list);
			});

			// 4. Build result rows
			const result: MatchTransitionRow[] = (matchData || []).map((m: any) => {
				const transition = matchIdMap.get(m.id);
				return {
					matchId: m.id,
					candidateName: m.candidates?.name || "—",
					jobTitle: m.jobs?.title || "—",
					score: m.score,
					currentStatus: m.status || "—",
					transitionDate: transition?.changed_at || "",
					history: historyByMatch.get(m.id) || [],
				};
			});

			// Sort by transition date desc
			result.sort((a, b) => new Date(b.transitionDate).getTime() - new Date(a.transitionDate).getTime());

			setRows(result);
		} catch (err) {
			// Silently handle — empty state will show
			setRows([]);
		} finally {
			setLoading(false);
		}
	}, [fromStage, toStage, dateFrom, dateTo, userId]);

	useEffect(() => {
		if (open)
			fetchData();
	}, [open, fetchData]);

	const handleNavigate = (matchId: string) => {
		onClose();
		navigate(`/matches/${matchId}`);
	};

	return (
		<Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
			<DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-lg">
						<Badge className={getStatusBadgeClass(fromStage)}>
							{t(`dashboard.kpi.stages.${fromStage}`, fromStage)}
						</Badge>
						<ArrowRight className="h-4 w-4 text-muted-foreground" />
						<Badge className={getStatusBadgeClass(toStage)}>
							{t(`dashboard.kpi.stages.${toStage}`, toStage)}
						</Badge>
						{!loading && (
							<span className="text-sm font-normal text-muted-foreground ml-2">
								({rows.length} {rows.length === 1 ? "match" : "matches"})
							</span>
						)}
					</DialogTitle>
				</DialogHeader>

				<div className="flex-1 overflow-y-auto min-h-0">
					{loading ? (
						<div className="flex items-center justify-center py-12">
							<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
						</div>
					) : rows.length === 0 ? (
						<div className="text-center text-muted-foreground py-12">
							{t("dashboard.kpi.noData")}
						</div>
					) : (
						<div className="space-y-1">
							{rows.map(row => {
								const isExpanded = expandedMatch === row.matchId;
								return (
									<div key={row.matchId} className="border rounded-lg overflow-hidden">
										{/* Match row */}
										<div className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors">
											{/* Expand toggle for history */}
											<button
												onClick={() => setExpandedMatch(isExpanded ? null : row.matchId)}
												className="shrink-0 p-0.5 rounded hover:bg-muted"
											>
												{isExpanded
													? <ChevronRight className="h-4 w-4 text-muted-foreground rotate-90 transition-transform" />
													: <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform" />}
											</button>

											{/* Score */}
											<div className="shrink-0 w-10 text-center">
												{row.score != null ? (
													<span className={`text-sm font-semibold ${row.score >= 70 ? "text-green-600" : row.score >= 40 ? "text-amber-600" : "text-red-500"}`}>
														{row.score}
													</span>
												) : (
													<span className="text-xs text-muted-foreground">—</span>
												)}
											</div>

											{/* Candidate + Job */}
											<div className="flex-1 min-w-0">
												<div className="text-sm font-medium truncate">{row.candidateName}</div>
												<div className="text-xs text-muted-foreground truncate">{row.jobTitle}</div>
											</div>

											{/* Current status */}
											<Badge className={`shrink-0 text-xs ${getStatusBadgeClass(row.currentStatus)}`}>
												{t(`dashboard.kpi.stages.${row.currentStatus}`, row.currentStatus)}
											</Badge>

											{/* Transition date */}
											<span className="shrink-0 text-xs text-muted-foreground w-20 text-right">
												{row.transitionDate
													? new Date(row.transitionDate).toLocaleDateString(undefined, { day: "numeric", month: "short" })
													: ""}
											</span>

											{/* Navigate button */}
											<Button
												variant="ghost"
												size="sm"
												className="shrink-0 h-7 w-7 p-0"
												onClick={() => handleNavigate(row.matchId)}
											>
												<ExternalLink className="h-3.5 w-3.5" />
											</Button>
										</div>

										{/* Expanded: full transition history */}
										{isExpanded && row.history.length > 0 && (
											<div className="border-t bg-muted/20 px-4 py-2.5">
												<div className="text-xs font-medium text-muted-foreground mb-2">
													{t("dashboard.kpi.transitionHistory", "Transition History")}
												</div>
												<div className="flex flex-wrap items-center gap-1">
													{row.history.map((h, i) => (
														<div key={i} className="flex items-center gap-1">
															{i === 0 && (
																<Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getStatusBadgeClass(h.from)}`}>
																	{t(`dashboard.kpi.stages.${h.from}`, h.from)}
																</Badge>
															)}
															<ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
															<div className="flex flex-col items-center">
																<Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${getStatusBadgeClass(h.to)}`}>
																	{t(`dashboard.kpi.stages.${h.to}`, h.to)}
																</Badge>
																<span className="text-[9px] text-muted-foreground mt-0.5">
																	{new Date(h.date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
																</span>
															</div>
														</div>
													))}
												</div>
											</div>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
